import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaGraduationCap, FaUsers, FaGlobe, FaAward, FaPlay, FaArrowRight, FaStar, FaCheckCircle, FaUniversity, FaBookOpen, FaLaptop, FaChalkboardTeacher } from 'react-icons/fa';
import './UniversityLanding.css';
import logo from '../../assets/logo.png';

const UniversityLanding = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const stats = [
    { icon: <FaUsers />, number: '50,000+', label: 'Students Worldwide' },
    { icon: <FaGraduationCap />, number: '200+', label: 'Programs Offered' },
    { icon: <FaGlobe />, number: '150+', label: 'Countries Represented' },
    { icon: <FaAward />, number: '95%', label: 'Employment Rate' },
  ];

  const programs = [
    {
      icon: <FaUniversity />,
      title: 'Undergraduate Programs',
      description: 'Comprehensive bachelor\'s degrees in various fields with hands-on learning experiences.',
      duration: '4 Years',
      courses: ['Business Administration', 'Computer Science', 'Engineering', 'Arts & Humanities'],
    },
    {
      icon: <FaBookOpen />,
      title: 'Graduate Programs',
      description: 'Advanced master\'s and doctoral programs designed for career advancement and research.',
      duration: '2-4 Years',
      courses: ['MBA', 'Data Science', 'Engineering Management', 'Research Programs'],
    },
    {
      icon: <FaLaptop />,
      title: 'Online Learning',
      description: 'Flexible online programs that fit your schedule and lifestyle.',
      duration: 'Flexible',
      courses: ['Digital Marketing', 'Web Development', 'Project Management', 'Healthcare'],
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Computer Science Graduate',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      text: 'The university provided me with excellent opportunities and a strong foundation for my career. The faculty is world-class and the resources are outstanding.',
      rating: 5,
    },
    {
      name: 'Michael Chen',
      role: 'Business Administration Student',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      text: 'I\'m impressed by the practical approach to learning and the industry connections. The career services team helped me secure an amazing internship.',
      rating: 5,
    },
    {
      name: 'Emily Rodriguez',
      role: 'Engineering Graduate',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      text: 'The research opportunities and state-of-the-art facilities exceeded my expectations. I\'m now working at a top tech company.',
      rating: 5,
    },
  ];

  const features = [
    'World-class faculty with industry experience',
    'State-of-the-art facilities and laboratories',
    'Global study abroad opportunities',
    'Comprehensive career services and placement',
    'Research and innovation centers',
    'Diverse and inclusive campus community',
    'Flexible learning options',
    'Strong alumni network',
  ];

  return (
    <div className="university-landing">
      {/* Hero Section */}
      <section className="university-landing-hero-section">
        <div className="university-landing-hero-background">
          <div className="university-landing-hero-overlay"></div>
        </div>
        <div className="university-landing-univ-hero-content">
          <div className="university-landing-hero-text">
            <h1 className="university-landing-hero-title">
                            Shape Your Future at <span className="university-landing-highlights">UlsaApp University</span>
            </h1>
            <p className="university-landing-hero-subtitle">
                            Join a community of innovators, leaders, and changemakers. Discover your potential with world-class education, cutting-edge research, and endless opportunities.
            </p>
            <div className="university-landing-hero-buttons">
              <button
                className="university-landing-btn-primary"
                onClick={() => navigate('/university-criteria')}
              >
                                Get Started <FaArrowRight />
              </button>
              <button className="university-landing-btn-secondary">
                <FaPlay /> Watch Our Story
              </button>
            </div>
          </div>
          <div className="university-landing-hero-images">
            <img
              src={logo}
              alt="UlsaApp Logo"
              className="university-landing-university-logo"
            />

          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="university-landing-stats-section">
        <div className="university-landing-container">
          <div className="university-landing-stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="university-landing-stat-item">
                <div className="university-landing-stat-icon">{stat.icon}</div>
                <div className="university-landing-stat-number">{stat.number}</div>
                <div className="university-landing-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="university-landing-programs-section">
        <div className="university-landing-container">
          <div className="university-landing-section-header">
            <h2>Academic Excellence</h2>
            <p>Choose from our diverse range of programs designed to prepare you for success</p>
          </div>
          <div className="university-landing-programs-grid">
            {programs.map((program, index) => (
              <div key={index} className="university-landing-program-card">
                <div className="university-landing-program-icon">{program.icon}</div>
                <h3>{program.title}</h3>
                <p>{program.description}</p>
                <div className="university-landing-program-duration">
                  <strong>Duration:</strong> {program.duration}
                </div>
                <div className="university-landing-program-courses">
                  <strong>Popular Courses:</strong>
                  <ul>
                    {program.courses.map((course, idx) => (
                      <li key={idx}>{course}</li>
                    ))}
                  </ul>
                </div>
                <button className="university-landing-btn-outline">Learn More</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="university-landing-features-section">
        <div className="university-landing-container">
          <div className="university-landing-features-content">
            <div className="university-landing-features-text">
              <h2>Why Choose UlsaApp University?</h2>
              <p>We provide an exceptional learning environment that fosters growth, innovation, and success.</p>
              <div className="university-landing-features-list">
                {features.map((feature, index) => (
                  <div key={index} className="university-landing-feature-item">
                    <FaCheckCircle className="university-landing-feature-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <button
                className="university-landing-btn-primary"
                onClick={() => navigate('/signup')}
              >
                                Start Your Journey <FaArrowRight />
              </button>
            </div>
            <div className="university-landing-features-image">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=500&h=400&fit=crop"
                alt="Students in Library"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="university-landing-testimonials-section">
        <div className="university-landing-container">
          <div className="university-landing-section-header">
            <h2>What Our Students Say</h2>
            <p>Hear from our graduates about their transformative experiences</p>
          </div>
          <div className="university-landing-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="university-landing-testimonial-card">
                <div className="university-landing-testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="university-landing-star" />
                  ))}
                </div>
                <p className="university-landing-testimonial-text">"{testimonial.text}"</p>
                <div className="university-landing-testimonial-author">
                  <img src={testimonial.image} alt={testimonial.name} />
                  <div>
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="university-landing-cta-section">
        <div className="university-landing-container">
          <div className="university-landing-cta-content">
            <h2>Ready to Transform Your Future?</h2>
            <p>Join thousands of students who have already taken the first step towards their dreams.</p>
            <div className="university-landing-cta-buttons">
              <button
                className="university-landing-btn-primary university-landing-btn-large"
                onClick={() => navigate('/signup')}
              >
                                Apply for Admission <FaArrowRight />
              </button>
              <button
                className="university-landing-btn-outline university-landing-btn-large"
                onClick={() => navigate('/home')}
              >
                                Explore Courses
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="university-landing-university-footer">
        <div className="university-landing-container">
          <div className="university-landing-footer-content">
            <div className="university-landing-footer-section">
              <h3>UlsaApp University</h3>
              <p>Empowering minds, shaping futures, and building tomorrow's leaders through excellence in education.</p>
            </div>
            <div className="university-landing-footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#programs">Programs</a></li>
                <li><a href="#admissions">Admissions</a></li>
                <li><a href="#research">Research</a></li>
                <li><a href="#campus">Campus Life</a></li>
              </ul>
            </div>
            <div className="university-landing-footer-section">
              <h4>Contact</h4>
              <p>123 University Ave<br />
                                Excellence City, EC 12345<br />
                                Phone: (555) 123-4567<br />
                                Email: info@excellence.edu</p>
            </div>
          </div>
          <div className="university-landing-footer-bottom">
            <p>&copy; 2024 UlsaApp University. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UniversityLanding; 